<template>
  <div class="inscription-indicators">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>

    <div v-else>
      <!-- KPI cards -->
      <div class="row">
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="kpi-card kpi-total">
            <div class="kpi-icon"><a-icon type="team" /></div>
            <div class="kpi-body">
              <div class="kpi-value">{{ totals.total }}</div>
              <div class="kpi-label">{{ $t("inscriptionStats.total") }}</div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="kpi-card kpi-enrolled">
            <div class="kpi-icon"><a-icon type="solution" /></div>
            <div class="kpi-body">
              <div class="kpi-value">{{ totals.enrolled }}</div>
              <div class="kpi-label">
                {{ $t("inscriptionStats.enrolled") }}
              </div>
              <div class="kpi-sub">{{ percent(totals.enrolled) }}%</div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="kpi-card kpi-affected">
            <div class="kpi-icon"><a-icon type="swap" /></div>
            <div class="kpi-body">
              <div class="kpi-value">{{ totals.affected }}</div>
              <div class="kpi-label">
                {{ $t("inscriptionStats.affected") }}
              </div>
              <div class="kpi-sub">{{ percent(totals.affected) }}%</div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="kpi-card kpi-unplaced">
            <div class="kpi-icon"><a-icon type="user-delete" /></div>
            <div class="kpi-body">
              <div class="kpi-value">{{ totals.unplaced }}</div>
              <div class="kpi-label">
                {{ $t("inscriptionStats.unplaced") }}
              </div>
              <div class="kpi-sub">{{ percent(totals.unplaced) }}%</div>
            </div>
          </div>
        </div>
      </div>

      <!-- charts -->
      <div class="row">
        <div class="col-lg-5 mb-3">
          <div class="chart-panel">
            <h6 class="panel-title">
              {{ $t("inscriptionStats.repartition") }}
            </h6>
            <apexchart
              type="donut"
              height="300"
              :options="donutOptions"
              :series="donutSeries"
            />
          </div>
        </div>
        <div class="col-lg-7 mb-3">
          <div class="chart-panel">
            <h6 class="panel-title">
              {{ $t("inscriptionStats.effectifParClasse") }}
            </h6>
            <a-empty
              v-if="!barSeries[0].data.length"
              :description="$t('inscriptionStats.aucuneDonnee')"
            />
            <apexchart
              v-else
              type="bar"
              :height="Math.max(300, barCategories.length * 34)"
              :options="barOptions"
              :series="barSeries"
            />
          </div>
        </div>
      </div>

      <!-- per-level breakdown -->
      <div class="chart-panel">
        <div
          class="d-flex justify-content-between align-items-center flex-wrap"
        >
          <h6 class="panel-title mb-0">
            {{ $t("inscriptionStats.parNiveau") }}
          </h6>
          <a-tooltip :title="$t('inscriptionStats.imbalanceHint')">
            <span class="legend-imbalance">
              <span class="dot"></span>
              {{ $t("inscriptionStats.imbalance") }}
            </span>
          </a-tooltip>
        </div>

        <a-empty
          v-if="!levelBreakdown.length"
          :description="$t('inscriptionStats.aucuneDonnee')"
        />

        <div v-for="lvl in levelBreakdown" :key="lvl.key" class="level-block">
          <div class="level-head">
            <div>
              <span class="level-name">{{ lvl.label }}</span>
              <a-tag color="blue" class="ml-2">
                {{ lvl.total }} {{ $t("inscriptionStats.eleves") }}
              </a-tag>
              <a-tag class="ml-1">
                {{ $t("inscriptionStats.moyenne") }}: {{ lvl.avg }}
              </a-tag>
              <a-tag
                v-if="lvl.imbalanced"
                color="red"
                class="ml-1"
              >
                <a-icon type="warning" />
                {{ $t("inscriptionStats.imbalanceTag") }} ({{ lvl.spread }})
              </a-tag>
            </div>
          </div>

          <!-- Affectation (level) classrooms — highlighted transit band -->
          <div
            v-if="lvl.affectation.length"
            class="affectation-band"
          >
            <div class="band-label">
              <a-icon type="swap" />
              {{ $t("inscriptionStats.bandAffectation") }}
            </div>
            <div
              v-for="c in lvl.affectation"
              :key="c._id"
              class="classroom-row is-affectation"
            >
              <div class="cr-name" :title="c.name">{{ c.name }}</div>
              <div class="cr-bar">
                <div
                  class="cr-bar-fill is-affectation"
                  :style="{ width: barWidth(c.count, lvl.barMax) + '%' }"
                ></div>
              </div>
              <div class="cr-count">{{ c.count }}</div>
            </div>
          </div>

          <!-- Normal classrooms -->
          <div v-if="lvl.normal.length" class="normal-band">
            <div class="band-label band-label-muted">
              <a-icon type="book" />
              {{ $t("inscriptionStats.bandNormal") }}
            </div>
            <div
              v-for="c in lvl.normal"
              :key="c._id"
              class="classroom-row"
            >
              <div class="cr-name" :title="c.name">{{ c.name }}</div>
              <div class="cr-bar">
                <div
                  class="cr-bar-fill"
                  :class="{
                    'is-min': lvl.imbalanced && c.count === lvl.min,
                    'is-max': lvl.imbalanced && c.count === lvl.max,
                  }"
                  :style="{ width: barWidth(c.count, lvl.barMax) + '%' }"
                ></div>
              </div>
              <div class="cr-count">{{ c.count }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import { getAllLevels } from "@/config/LevelConstants";

const IMBALANCE_THRESHOLD = 8;

export default {
  name: "InscriptionIndicators",
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding && this.activeBuilding.countryCode;
    },
    allLevels() {
      return getAllLevels(this.$t.bind(this), this.countryCode);
    },
    normalClassrooms() {
      return this.classrooms.filter(
        (c) =>
          c.status !== "inactive" && c.classroomType !== "affectation"
      );
    },
    barCategories() {
      return this.normalClassrooms
        .slice()
        .sort((a, b) => b.count - a.count)
        .map((c) => c.name);
    },
    barValues() {
      return this.normalClassrooms
        .slice()
        .sort((a, b) => b.count - a.count)
        .map((c) => c.count);
    },
    barSeries() {
      return [
        {
          name: this.$t("inscriptionStats.eleves"),
          data: this.barValues,
        },
      ];
    },
    barOptions() {
      return {
        chart: { toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            distributed: true,
            dataLabels: { position: "top" },
          },
        },
        dataLabels: {
          enabled: true,
          offsetX: 18,
          style: { colors: ["#4a5568"], fontSize: "12px" },
        },
        legend: { show: false },
        xaxis: { categories: this.barCategories },
        grid: { borderColor: "#eef0f3" },
        tooltip: {
          y: {
            formatter: (v) => `${v} ${this.$t("inscriptionStats.eleves")}`,
          },
        },
      };
    },
    donutSeries() {
      return [
        this.totals.enrolled || 0,
        this.totals.affected || 0,
        this.totals.unplaced || 0,
      ];
    },
    donutOptions() {
      return {
        labels: [
          this.$t("inscriptionStats.enrolled"),
          this.$t("inscriptionStats.affected"),
          this.$t("inscriptionStats.unplaced"),
        ],
        colors: ["#1890ff", "#fa8c16", "#bfbfbf"],
        legend: { position: "bottom" },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${Math.round(val)}%`,
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  label: this.$t("inscriptionStats.total"),
                  formatter: () => String(this.totals.total || 0),
                },
              },
            },
          },
        },
      };
    },
    levelBreakdown() {
      const groups = {};
      this.classrooms
        .filter((c) => c.status !== "inactive")
        .forEach((c) => {
        const key =
          c.level === undefined || c.level === null
            ? "undefined"
            : String(c.level);
        if (!groups[key]) groups[key] = [];
        groups[key].push(c);
      });

      return Object.keys(groups)
        .sort((a, b) => {
          if (a === "undefined") return 1;
          if (b === "undefined") return -1;
          return Number(a) - Number(b);
        })
        .map((key) => {
          const classrooms = groups[key]
            .slice()
            .sort((a, b) => b.count - a.count);
          const normal = classrooms.filter(
            (c) => c.classroomType !== "affectation"
          );
          const affectation = classrooms.filter(
            (c) => c.classroomType === "affectation"
          );
          const counts = normal.map((c) => c.count);
          const total = counts.reduce((s, n) => s + n, 0);
          const max = counts.length ? Math.max(...counts) : 0;
          const min = counts.length ? Math.min(...counts) : 0;
          const allCounts = classrooms.map((c) => c.count);
          const barMax = allCounts.length ? Math.max(...allCounts) : 0;
          const avg = normal.length
            ? Math.round((total / normal.length) * 10) / 10
            : 0;
          const spread = max - min;
          return {
            key,
            label:
              key === "undefined"
                ? this.$t("scolarite.other")
                : this.allLevels[key] || key,
            normal,
            affectation,
            total,
            avg,
            max,
            min,
            barMax,
            spread,
            imbalanced: normal.length >= 2 && spread >= IMBALANCE_THRESHOLD,
          };
        });
    },
  },
  data() {
    return {
      loading: true,
      totals: { total: 0, enrolled: 0, affected: 0, unplaced: 0 },
      classrooms: [],
    };
  },
  created() {
    this.fetchStats();
  },
  methods: {
    percent(value) {
      const t = this.totals.total || 0;
      if (!t) return 0;
      return Math.round((value / t) * 100);
    },
    barWidth(count, max) {
      if (!max) return 0;
      return Math.max(4, Math.round((count / max) * 100));
    },
    fetchStats() {
      this.loading = true;
      apiClient
        .get("/classrooms/inscription-stats")
        .then((res) => {
          this.totals = res.data.totals || this.totals;
          this.classrooms = res.data.classrooms || [];
        })
        .catch(() => {
          this.$message.error(this.$t("inscriptionStats.loadError"));
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.inscription-indicators {
  padding: 4px;
}
.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  height: 100%;
}
.kpi-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #fff;
}
.kpi-total .kpi-icon {
  background: linear-gradient(135deg, #722ed1, #9254de);
}
.kpi-enrolled .kpi-icon {
  background: linear-gradient(135deg, #1890ff, #40a9ff);
}
.kpi-affected .kpi-icon {
  background: linear-gradient(135deg, #fa8c16, #ffa940);
}
.kpi-unplaced .kpi-icon {
  background: linear-gradient(135deg, #8c8c8c, #bfbfbf);
}
.kpi-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: #1f2733;
}
.kpi-label {
  color: #8a94a6;
  font-size: 13px;
}
.kpi-sub {
  font-size: 12px;
  font-weight: 600;
  color: #52c41a;
}
.chart-panel {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}
.panel-title {
  font-weight: 600;
  color: #1f2733;
  margin-bottom: 14px;
}
.legend-imbalance {
  font-size: 12px;
  color: #8a94a6;
  cursor: help;
}
.legend-imbalance .dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff4d4f;
  margin-right: 4px;
}
.level-block {
  padding: 12px 0;
  border-top: 1px solid #f0f0f0;
}
.level-head {
  margin-bottom: 10px;
}
.level-name {
  font-weight: 600;
  font-size: 15px;
  color: #1f2733;
}
.affectation-band {
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  /* Accent without shifting layout (keeps bars aligned with normal rows). */
  box-shadow: inset 3px 0 0 #fa8c16;
  padding: 10px 0;
  margin-bottom: 10px;
}
.normal-band {
  /* Same border box as affectation so columns stay vertically aligned. */
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0;
}
.band-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #d46b08;
  margin-bottom: 8px;
  padding: 0 12px;
}
.band-label-muted {
  color: #8a94a6;
  margin-bottom: 6px;
}
.classroom-row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr) 40px;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  padding: 0 12px;
}
.classroom-row.is-affectation:last-child {
  margin-bottom: 0;
}
.cr-name {
  font-size: 13px;
  color: #4a5568;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-affectation .cr-name {
  color: #ad6800;
  font-weight: 500;
}
.cr-bar {
  background: #f2f4f7;
  border-radius: 6px;
  height: 18px;
  overflow: hidden;
  min-width: 0;
}
.cr-bar-fill {
  height: 100%;
  background: #1890ff;
  border-radius: 6px;
  transition: width 0.3s ease;
}
.cr-bar-fill.is-affectation {
  background: #fa8c16;
}
.cr-bar-fill.is-max {
  background: #ff4d4f;
}
.cr-bar-fill.is-min {
  background: #faad14;
}
.cr-count {
  text-align: right;
  font-weight: 600;
  color: #1f2733;
}
</style>
